import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { CheckCircle2, Home, CalendarDays, Users, TrendingUp, AlertCircle } from "lucide-react";
import { supabase } from "@/lib/supabaseClient";

function HeditoLogo({ size = 40 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 36 36" fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect width="36" height="36" rx="9" fill="#c87045" />
      <rect x="7" y="8" width="5.5" height="20" rx="2" fill="white"/>
      <rect x="23.5" y="8" width="5.5" height="20" rx="2" fill="white"/>
      <rect x="7" y="15.5" width="22" height="5" rx="2" fill="white"/>
    </svg>
  );
}

const FEATURES = [
  { icon: Home,         text: "Gestisci strutture e camere" },
  { icon: CalendarDays, text: "Prenotazioni e calendario" },
  { icon: Users,        text: "Team: host, collaboratori, pulizie" },
  { icon: TrendingUp,   text: "Finanze e report completi" },
];

export default function Register() {
  const [fullName, setFullName] = useState("");
  const [email, setEmail]       = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState<string | null>(null);
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    
    const { error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: {
          full_name: fullName,
        },
      },
    });
    
    if (error) {
      setError(error.message);
      setLoading(false);
      return;
    }
    
    // Registration successful - redirect to login
    // User may need to confirm email depending on Supabase settings
    navigate("/login");
  };

  return (
    <div className="min-h-screen flex bg-background">
      {/* Left panel */}
      <div className="hidden lg:flex flex-col justify-between w-[420px] bg-foreground text-white p-10 flex-shrink-0">
        <div>
          <div className="flex items-center gap-3 mb-12">
            <HeditoLogo size={40} />
            <span className="text-xl font-bold tracking-tight">Hedita</span>
          </div>
          <h2 className="text-3xl font-bold leading-snug mb-3">Inizia gratis, gestisci tutto da un'unica app.</h2>
          <p className="text-white/60 text-sm leading-relaxed">Hedita è pensata per host con 1-2 strutture ricettive. Semplice, veloce, completa.</p>
          <div className="mt-8 space-y-4">
            {FEATURES.map(({ icon: Icon, text }) => (
              <div key={text} className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-white/10 flex items-center justify-center flex-shrink-0">
                  <Icon className="h-4 w-4 text-primary" />
                </div>
                <span className="text-sm text-white/80">{text}</span>
              </div>
            ))}
          </div>
        </div>
        <p className="text-white/30 text-xs">© 2026 Hedita · Tutti i diritti riservati</p>
      </div>

      {/* Right panel */}
      <div className="flex-1 flex items-center justify-center p-6">
        <div className="w-full max-w-sm">
          <div className="flex items-center gap-2.5 mb-8 lg:hidden">
            <HeditoLogo size={36} />
            <span className="text-lg font-bold text-foreground">Hedita</span>
          </div>

          <h1 className="text-2xl font-bold text-foreground mb-1">Crea il tuo account</h1>
          <p className="text-sm text-muted-foreground mb-6">Inizia a gestire le tue strutture con Hedita</p>

          {/* Error message */}
          {error && (
            <div className="flex items-start gap-2.5 bg-red-50 border border-red-200 rounded-xl px-4 py-3 mb-6">
              <AlertCircle className="h-4 w-4 text-red-600 mt-0.5 flex-shrink-0" />
              <p className="text-xs text-red-800 leading-relaxed">{error}</p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-foreground mb-1.5">Nome completo</label>
              <input
                type="text" required
                placeholder="Mario Rossi"
                value={fullName}
                onChange={e => setFullName(e.target.value)}
                className="w-full px-3.5 py-2.5 rounded-xl border border-border bg-white text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-colors"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground mb-1.5">Email</label>
              <input
                type="email" required
                placeholder="nome@esempio.com"
                value={email}
                onChange={e => setEmail(e.target.value)}
                className="w-full px-3.5 py-2.5 rounded-xl border border-border bg-white text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-colors"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground mb-1.5">Password</label>
              <input
                type="password" required
                placeholder="••••••••"
                value={password}
                onChange={e => setPassword(e.target.value)}
                className="w-full px-3.5 py-2.5 rounded-xl border border-border bg-white text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-colors"
              />
            </div>
            <button
              type="submit"
              disabled={loading}
              className="w-full py-2.5 rounded-xl bg-primary text-white text-sm font-semibold hover:bg-primary/90 transition-colors disabled:opacity-60"
            >
              {loading ? "Creazione account..." : "Registrati"}
            </button>
          </form>

          <p className="mt-5 text-center text-sm text-muted-foreground">
            Hai già un account?{" "}
            <Link to="/login" className="text-primary font-medium hover:underline">Accedi</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
